/**
 * Firesite SSO Application Entry Point
 * Initializes the single sign-on service and manages the application lifecycle
 */

import './assets/css/main.css';
import { initializeServices } from './services/index.js';
import { GlobalEvents, Events } from './core/events/index.js';
import { authService } from './services/auth-service.js';
// Import Firebase directly for logout
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signOut } from 'firebase/auth';
import { firebaseConfig } from './core/config/firebase.js';
// healthService import removed for browser compatibility

// Mark CSS as loaded immediately when this module loads
document.body?.classList.add('css-loaded');

// Handle API routes in development
if (import.meta.env.DEV) {
  // Intercept /api/health requests
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const [url] = args;
    
    if (typeof url === 'string' && url.includes('/api/health')) {
      // Return health status directly
      return new Response(JSON.stringify({
        status: 'healthy',
        service: 'firesite-sso',
        timestamp: new Date().toISOString(),
        environment: 'development',
        port: 4820,
        version: '1.0.0'
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }
    
    return originalFetch.apply(this, args);
  };
}

class FiresiteSSO {
  constructor() {
    this.initialized = false;
    this.elements = {};
  }

  /**
   * Initialize the application
   */
  async init() {
    if (this.initialized) return;

    try {
      console.log('Firesite SSO initializing...');

      // Check if this is a logout request BEFORE initializing Firebase
      if (window.location.pathname.includes('/logout')) {
        await this._handleLogout();
        return; // Don't continue initialization
      }

      // Parse auth parameters from URL
      this._parseAuthParams();

      // Cache DOM elements
      this._cacheElements();

      // Initialize all services
      await initializeServices();

      // Set up event listeners
      this._setupEventListeners();

      // Set up initial UI based on auth state
      this._setupInitialUI();

      this.initialized = true;
      console.log('Firesite SSO initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Firesite SSO:', error);
      this._showError('Failed to initialize application. Please refresh the page.');
    }
  }

  /**
   * Handle logout - clear Firebase session WITHOUT triggering auth state listeners
   */
  async _handleLogout() {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return');

    console.log('🚪 Logout requested, return URL:', returnUrl);

    // Clear sessionStorage FIRST to prevent auth state race conditions
    sessionStorage.clear();
    console.log('✅ SessionStorage cleared');

    try {
      // Initialize Firebase directly WITHOUT setting up listeners
      // This avoids the race condition where onAuthStateChanged fires before signOut
      let app;
      const existingApps = getApps();
      if (existingApps.length > 0) {
        app = existingApps[0];
      } else {
        app = initializeApp(firebaseConfig);
      }

      const auth = getAuth(app);

      console.log('Firebase current user BEFORE logout:', auth.currentUser ? auth.currentUser.email : 'none');

      // Sign out directly - this clears IndexedDB refresh tokens
      if (auth.currentUser) {
        await signOut(auth);
        console.log('✅ Firebase signOut() completed');

        // Wait longer to ensure IndexedDB is actually cleared
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verify signOut worked
        console.log('Firebase current user AFTER logout:', auth.currentUser ? auth.currentUser.email : 'NONE - LOGGED OUT');
      } else {
        console.log('ℹ️ No Firebase user to sign out');
      }

    } catch (error) {
      console.error('❌ Logout error:', error);
      // Continue with redirect even if signOut fails
    }
    
    // Redirect back to calling service
    if (returnUrl) {
      console.log('🔄 Redirecting to:', returnUrl);
      window.location.href = returnUrl;
    } else {
      // No return URL, redirect to home
      console.log('✅ Logout complete, redirecting to home');
      window.location.href = '/';
    }
  }

  /**
   * Parse authentication parameters from URL
   * Handles returnUrl and service parameters from redirecting services
   * @private
   */
  _parseAuthParams() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl') || urlParams.get('return');
      const serviceName = urlParams.get('service');
      const state = urlParams.get('state');

      console.log('🔍 Parsing auth parameters:', { returnUrl, serviceName, state });

      // Store for use after authentication
      if (returnUrl) {
        sessionStorage.setItem('auth_return_url', returnUrl);
        sessionStorage.setItem('auth_service_name', serviceName || 'unknown');
        sessionStorage.setItem('auth_state', state || '');
        
        console.log('✅ Auth parameters stored for redirect:', {
          returnUrl,
          serviceName: serviceName || 'unknown'
        });
      } else {
        // Clear any existing auth parameters if no returnUrl
        sessionStorage.removeItem('auth_return_url');
        sessionStorage.removeItem('auth_service_name');
        sessionStorage.removeItem('auth_state');
        
        console.log('🔄 Direct SSO access - no redirect parameters');
      }
    } catch (error) {
      console.error('❌ Error parsing auth parameters:', error);
    }
  }

  /**
   * Cache DOM elements
   */
  _cacheElements() {
    this.elements = {
      loadingState: document.getElementById('loading-state'),
      loginForm: document.getElementById('login-form'),
      authenticatedState: document.getElementById('authenticated-state'),
      errorMessage: document.getElementById('error-message'),
      successMessage: document.getElementById('success-message'),
      errorText: document.getElementById('error-text'),
      successText: document.getElementById('success-text'),
      userInfo: document.getElementById('user-info'),
      verifyEmailBtn: document.getElementById('verify-email-btn'),
      signoutBtn: document.getElementById('signout-btn'),
      forgotPasswordLink: document.getElementById('forgot-password-link'),
      serviceContext: document.getElementById('service-context'),
      serviceContextText: document.getElementById('service-context-text')
    };
  }

  /**
   * Set up global event listeners
   */
  _setupEventListeners() {
    // Auth state changes
    GlobalEvents.on(Events.AUTH_STATE_CHANGED, (data) => {
      this._handleAuthStateChange(data);
    });

    // Auth errors
    GlobalEvents.on(Events.AUTH_ERROR, (data) => {
      this._showError(data.error);
    });

    // Service loading states
    GlobalEvents.on(Events.SERVICE_LOADING, (data) => {
      if (data.service === 'AuthService') {
        this._showLoading(true);
      }
    });

    // DOM event listeners
    this._attachDOMListeners();
  }

  /**
   * Attach DOM event listeners
   */
  _attachDOMListeners() {
    // Login form submission
    if (this.elements.loginForm) {
      this.elements.loginForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this._handleLogin(e);
      });
    }

    // Sign out button
    if (this.elements.signoutBtn) {
      this.elements.signoutBtn.addEventListener('click', async () => {
        await this._handleSignOut();
      });
    }

    // Verify email button
    if (this.elements.verifyEmailBtn) {
      this.elements.verifyEmailBtn.addEventListener('click', async () => {
        await this._handleVerifyEmail();
      });
    }

    // Forgot password link
    if (this.elements.forgotPasswordLink) {
      this.elements.forgotPasswordLink.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleForgotPassword();
      });
    }
  }

  /**
   * Set up initial UI
   */
  _setupInitialUI() {
    // Update service context display
    this._updateServiceContext();
    
    // Show loading state initially while Firebase auth state resolves
    this._showLoading(true);
    
    // Firebase auth state listener will handle showing the correct UI
    // This prevents the flash between login form and authenticated state
  }

  /**
   * Handle authentication state changes
   */
  _handleAuthStateChange(data) {
    const { user, isAuthenticated } = data;
    
    // Hide loading state when auth state is determined
    this._showLoading(false);
    
    if (isAuthenticated && user) {
      // Check if this is a redirect scenario and user is already authenticated
      const returnUrl = sessionStorage.getItem('auth_return_url');
      
      if (returnUrl) {
        console.log('🔄 User already authenticated with redirect URL - triggering redirect flow');
        // User is already authenticated but came via redirect URL
        // Emit event to trigger redirect authentication flow
        GlobalEvents.emit(Events.AUTH_REDIRECT_REQUIRED, {
          user,
          returnUrl,
          serviceName: sessionStorage.getItem('auth_service_name')
        });
      } else {
        // Normal authenticated state - show authenticated UI
        this._showAuthenticatedState(user);
      }
    } else {
      this._showLoginForm();
    }
  }

  /**
   * Show loading state
   */
  _showLoading(show = true) {
    if (show) {
      this._hideAllStates();
      this.elements.loadingState?.classList.remove('hidden');
    } else {
      this.elements.loadingState?.classList.add('hidden');
    }
  }

  /**
   * Show login form
   */
  _showLoginForm() {
    this._hideAllStates();
    this._hideMessages();
    this._updateServiceContext();
    this.elements.loginForm?.classList.remove('hidden');
  }

  /**
   * Show authenticated state
   */
  _showAuthenticatedState(user) {
    this._hideAllStates();
    this._hideMessages();
    
    if (this.elements.authenticatedState) {
      this.elements.authenticatedState.classList.remove('hidden');
      
      // Update user info
      if (this.elements.userInfo) {
        this.elements.userInfo.textContent = `${user.email} • User ID: ${user.uid}`;
      }
      
      // Show/hide verify email button
      if (this.elements.verifyEmailBtn) {
        if (user.emailVerified) {
          this.elements.verifyEmailBtn.classList.add('hidden');
        } else {
          this.elements.verifyEmailBtn.classList.remove('hidden');
        }
      }
    }
  }

  /**
   * Hide all UI states
   */
  _hideAllStates() {
    this.elements.loadingState?.classList.add('hidden');
    this.elements.loginForm?.classList.add('hidden');
    this.elements.authenticatedState?.classList.add('hidden');
  }

  /**
   * Hide all messages
   */
  _hideMessages() {
    this.elements.errorMessage?.classList.add('hidden');
    this.elements.successMessage?.classList.add('hidden');
  }

  /**
   * Update service context display to show which service user is logging in for
   * @private
   */
  _updateServiceContext() {
    try {
      const serviceName = sessionStorage.getItem('auth_service_name');
      
      if (serviceName && serviceName !== 'unknown' && this.elements.serviceContext && this.elements.serviceContextText) {
        // Format service name for display
        const displayName = this._formatServiceName(serviceName);
        this.elements.serviceContextText.textContent = `Logging in for ${displayName}`;
        this.elements.serviceContext.classList.remove('hidden');
        
        console.log('🎯 Service context displayed:', displayName);
      } else {
        // No service context or unknown service, hide the context display
        this.elements.serviceContext?.classList.add('hidden');
        
        if (!serviceName) {
          console.log('🔍 Direct SSO access - no service context');
        }
      }
    } catch (error) {
      console.error('❌ Error updating service context:', error);
      this.elements.serviceContext?.classList.add('hidden');
    }
  }

  /**
   * Format service name for user-friendly display
   * @param {string} serviceName - Raw service name
   * @returns {string} Formatted display name
   * @private
   */
  _formatServiceName(serviceName) {
    // Handle common Firesite service name patterns
    const serviceDisplayNames = {
      'firesite-project-service': 'Firesite Project Manager',
      'firesite-chat-service': 'Firesite Chat',
      'firesite-mcp-max': 'Firesite MCP Max',
      'project-service': 'Project Manager',
      'chat-service': 'Chat Service',
      'mcp-max': 'MCP Max'
    };

    // Return mapped name or capitalize the original
    return serviceDisplayNames[serviceName] || 
           serviceName.split('-')
                     .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                     .join(' ');
  }

  /**
   * Handle login form submission
   */
  async _handleLogin(e) {
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    
    if (!email || !password) {
      this._showError('Please enter both email and password');
      return;
    }

    this._showLoading(true);
    
    try {
      const result = await authService.signIn(email, password);
      
      if (!result.success) {
        this._showLoading(false);
        this._showError(result.error);
      }
      // Success is handled by auth state change event
    } catch (error) {
      this._showLoading(false);
      this._showError('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Handle sign out
   */
  async _handleSignOut() {
    try {
      await authService.signOut();
      // Success is handled by auth state change event
    } catch (error) {
      this._showError('Failed to sign out. Please try again.');
    }
  }

  /**
   * Handle verify email
   */
  async _handleVerifyEmail() {
    try {
      const result = await authService.sendVerificationEmail();
      
      if (result.success) {
        this._showSuccess('Verification email sent! Please check your inbox.');
      } else {
        this._showError(result.error);
      }
    } catch (error) {
      this._showError('Failed to send verification email. Please try again.');
    }
  }

  /**
   * Handle forgot password
   */
  _handleForgotPassword() {
    const email = this.elements.loginForm?.querySelector('#email')?.value;
    
    if (!email) {
      this._showError('Please enter your email address first');
      return;
    }

    // Show confirmation dialog
    if (confirm(`Send password reset email to ${email}?`)) {
      this._sendPasswordReset(email);
    }
  }

  /**
   * Send password reset email
   */
  async _sendPasswordReset(email) {
    try {
      const result = await authService.sendPasswordResetEmail(email);
      
      if (result.success) {
        this._showSuccess(`Password reset email sent to ${email}. Please check your inbox.`);
      } else {
        this._showError(result.error);
      }
    } catch (error) {
      this._showError('Failed to send password reset email. Please try again.');
    }
  }

  /**
   * Show error message
   */
  _showError(message) {
    console.error('Error:', message);
    
    if (this.elements.errorText && this.elements.errorMessage) {
      this.elements.errorText.textContent = message;
      this.elements.errorMessage.classList.remove('hidden');
      this.elements.successMessage?.classList.add('hidden');
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.elements.errorMessage?.classList.add('hidden');
      }, 5000);
    }
  }

  /**
   * Show success message
   */
  _showSuccess(message) {
    console.log('Success:', message);
    
    if (this.elements.successText && this.elements.successMessage) {
      this.elements.successText.textContent = message;
      this.elements.successMessage.classList.remove('hidden');
      this.elements.errorMessage?.classList.add('hidden');
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.elements.successMessage?.classList.add('hidden');
      }, 5000);
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  // Mark CSS as loaded to prevent FOUC
  document.body.classList.add('css-loaded');
  
  const app = new FiresiteSSO();
  await app.init();
});

// Export for debugging
window.FiresiteSSO = FiresiteSSO;